import { afterNextRender, Component, computed, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';

import { PlacedOrderPage } from '../../model/placed-order-page';
import { AnalyticsService } from '../../service/analytics.service';
import { SeoService } from '../../service/seo.service';
import { WebsiteService } from '../../service/website.service';
import { MoneyPipe } from '../../money.pipe';

const CLOSED = ['CANCELED', 'EXPIRED'];

/** The `placeorder` template with its `order-details` partial. */
@Component({
  selector: 'place-order-page',
  imports: [RouterLink, MoneyPipe],
  host: {
    style: 'display: contents'
  },
  template: `
    <!-- Title Page -->
    <section class="bg-title-page p-t-15 p-b-16 flex-col-c-m">
      <h2 class="l-text2 t-center">
        {{placed() ? 'Замовлення здійснено' : 'Замовлення'}}
      </h2>
    </section>

    <!-- Cart -->
    <section class="cart bgwhite p-t-70 p-b-70">
      <div class="container">
        @if (data().order; as order) {
          <h3 class="m-text26 p-t-15 p-b-16">
            @if (closed()) {
              Замовлення <strong>№{{order.code}}</strong> скасовано.
            } @else if (unpaid()) {
              Замовлення <strong>№{{order.code}}</strong> збережене, але оплату ще не підтверджено.
            } @else {
              Все гаразд. Ваше замовлення <strong>№{{order.code}}</strong> прийнято в опрацювання.
            }
          </h3>
          <div class="row">
            <div class="col-lg bo9 p-l-40 p-r-40 p-t-30 p-b-8 m-t-30 m-r-3 m-l-3 p-lr-15-sm">
              <h5 class="m-text20 p-b-24">
                Товари
              </h5>

              <div class="flex-w flex-sb-m p-b-12">
                @for (product of order.products; track $index) {
                  <span class="s-text18 w-size19 w-full-sm">
                    {{product.name}}
                  </span>

                  <span class="m-text21 w-size20 w-full-sm">
                    <small>{{product.price | money}} x {{product.quantity}}</small>
                  </span>
                }
              </div>

              <div class="flex-w flex-sb-m p-t-15 p-b-12">
                <span class="m-text22 w-size19 w-full-sm">
                  Загалом:
                </span>

                <span class="m-text21 w-size20 w-full-sm">
                  {{order.total | money}}
                </span>
              </div>
            </div>

            @if (order.shipping; as shipping) {
              <div class="col-lg bo9 p-l-40 p-r-40 p-t-30 p-b-8 m-t-30 m-r-3 m-l-3 p-lr-15-sm">
                <h5 class="m-text20 p-b-24">
                  Дані доставки
                </h5>

                <div class="flex-w flex-sb p-t-15 p-b-20">
                  <span class="s-text18 w-size19 w-full-sm">Служба доставки:</span>
                  <div class="w-size20 w-full-sm">
                    <p class="s-text8 p-b-23">{{shipping.shippingCompany}}</p>
                  </div>

                  <span class="s-text18 w-size19 w-full-sm">Одержувач:</span>
                  <div class="w-size20 w-full-sm">
                    <p class="s-text8 p-b-23">{{shipping.address.firstName}} {{shipping.address.lastName}}</p>
                  </div>

                  <span class="s-text18 w-size19 w-full-sm">Телефон:</span>
                  <div class="w-size20 w-full-sm">
                    <p class="s-text8 p-b-23"><b>{{shipping.address.phone}}</b></p>
                  </div>

                  @if (shipping.address.city) {
                    <span class="s-text18 w-size19 w-full-sm">Місто:</span>
                    <div class="w-size20 w-full-sm">
                      <p class="s-text8 p-b-23">{{shipping.address.city}}</p>
                    </div>
                  }

                  @if (shipping.address.street) {
                    <span class="s-text18 w-size19 w-full-sm">Адреса:</span>
                    <div class="w-size20 w-full-sm">
                      <p class="s-text8 p-b-23">{{shipping.address.street}}</p>
                    </div>
                  }

                  @if (shipping.paymentOption) {
                    <span class="s-text18 w-size19 w-full-sm">Оплата:</span>
                    <div class="w-size20 w-full-sm">
                      <p class="s-text8 p-b-23">{{shipping.paymentOption}}</p>
                    </div>
                  }

                  @if (shipping.comment) {
                    <span class="s-text18 w-size19 w-full-sm">Коментар:</span>
                    <div class="w-size20 w-full-sm">
                      <p class="s-text8 p-b-23">{{shipping.comment}}</p>
                    </div>
                  }
                </div>
              </div>
            }
          </div>
          @if (placed()) {
            <p class="p-b-28">
              <small>
                Будь-ласка, перевірте Ваш номер телефону у замовленні. Якщо він введений неправильно,
                повідомте цю інформацію нашому менеджеру за телефоном
                <a class="s-text7" [href]="'tel:' + website().phone">{{website().phone}}</a>.
              </small>
            </p>
            <p class="p-b-28 m-t-15">
              Очікуйте дзвінка нашого менеджера для підтвердження замовлення.
            </p>
          }
          <!-- No link back to the checkout: placing it again cancels this order, and the bank may
               still be processing its card. -->
          @if (closed()) {
            <p class="p-b-28 m-t-15">
              Якщо у вас є запитання щодо цього замовлення, зателефонуйте нам:
              <a class="s-text7" [href]="'tel:' + website().phone">{{website().phone}}</a>.
            </p>
          }
          @if (unpaid()) {
            <p class="p-b-28 m-t-15">
              Якщо оплата пройшла, ми підтвердимо замовлення, щойно її отримаємо. Якщо ні - зателефонуйте
              нам, і ми допоможемо його завершити:
              <a class="s-text7" [href]="'tel:' + website().phone">{{website().phone}}</a>.
            </p>
          }
        } @else {
          <!-- The API answers any code, and names the order only to the visitor who placed it. -->
          <h3 class="m-text26 p-t-15 p-b-16">
            Не вдалося знайти замовлення <strong>№{{data().orderCode}}</strong>.
          </h3>
          <p class="p-b-28 m-t-15">
            Якщо ви щойно його оформили, зателефонуйте нам:
            <a class="s-text7" [href]="'tel:' + website().phone">{{website().phone}}</a>.
          </p>
        }
        <p class="p-b-28">
          Завжди ваші, <a routerLink="/">{{website().name}}</a>.
        </p>
        <p class="p-b-28">
          <a routerLink="/">&lt; Повернутись на головну</a>
        </p>
      </div>
    </section>
    `
})
export class PlaceOrderPageComponent {

  readonly data = input.required<PlacedOrderPage>();

  readonly website = inject(WebsiteService).current;

  readonly closed = computed(() => CLOSED.includes(this.data().order?.status ?? ''));
  readonly unpaid = computed(() => this.data().order?.status === 'AWAITING_PAYMENT');
  readonly placed = computed(() => !!this.data().order && !this.closed() && !this.unpaid());

  constructor() {
    // The API answers with the order code only; the heading is the storefront's own wording.
    const seo = inject(SeoService);
    seo.page(computed(() => ({ ...this.data(), title: this.placed() ? 'Замовлення прийнято' : 'Замовлення' })), true);
    const analytics = inject(AnalyticsService);
    afterNextRender(() => {
      const order = this.data().order;
      if (order && this.placed()) {
        analytics.event('purchase', {
          transaction_id: order.code,
          value: order.total.value,
          currency: order.total.currency,
          items: order.products.map((product, index) => ({
            item_id: product.modelId,
            item_name: product.name,
            index,
            price: product.price.value,
            quantity: product.quantity
          }))
        });
      }
    });
  }

}
